import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpService } from '../http.service';
import { JobTitleCompetenceModel } from '../models/hiring/jobTitleCompetence.model';
import { JobTitleCompetenceLevelModel } from '../models/hiring/jobTitleCompetenceLevel.model';
import { Response } from '../models/response';
import { ServiceBase } from '../servicebase';

@Injectable()
export class JobTitleCompetenceService extends ServiceBase<JobTitleCompetenceModel> {
  constructor(http: HttpService) {
    super('JobTitleCompetence', http, environment.apiUrl);
  }

  createEntity(input: any): JobTitleCompetenceModel {
    return JobTitleCompetenceModel.create(input);
  }

  override get(id: string, disableLoading = false): Observable<Response<JobTitleCompetenceModel>> {
    return this.http
      .get(`${this.endpoint}/${id}`, undefined, this.baseUrl, undefined, disableLoading)
      .pipe(
        map(res => {
          const response = new Response<JobTitleCompetenceModel>();
          response.data = this.createEntity(res.body) as JobTitleCompetenceModel;
          (response as any).rawBody = res.body;
          return response;
        })
      );
  }

  getLevels(): Observable<JobTitleCompetenceLevelModel[]> {
    return this.http
      .get(`${this.endpoint}/list-levels?$orderBy=type asc`, null, null, null, true)
      .pipe(
        map((res: any) => {
        const levels = res.body || [];
        return levels.map((level: any) => JobTitleCompetenceLevelModel.create(level));
      })
    );
  }

  maxLevelCompetenciesList(): Observable<JobTitleCompetenceModel[]> {
    return this.http
      .get(`${this.endpoint}/max-level-competencies-list`, null, null, null, true)
      .pipe(
        map((res: any) => {
          const levels = res.body || [];
          return levels.map((level: any) => JobTitleCompetenceModel.create(level));
        })
      );
  }

  getLevel(id: string): Observable<JobTitleCompetenceLevelModel> {
    return this.http
      .get(`${this.endpoint}/level/${id}`, null, null, null, true)
      .pipe(map((res: any) => JobTitleCompetenceLevelModel.create(res.body)));
  }

  saveCompetenceLevels(data: JobTitleCompetenceLevelModel[]): Observable<any> {
    return this.http.put(`${this.endpoint}/levels`, data);
  }

  getOptionsByParent(parentId: string): Observable<any> {
    return this.http
      .get(`${this.endpoint}/options-by-parent/${parentId}`, null, null, null, true)
      .pipe(map(res => res));
  }

  listFromCompetenceMatrix(params: {
    JobTitleGroupId?: string;
    JobTitleFamilyId?: string;
    JobTitleId?: string;
    JobTitleSeniorizationId?: string;
  }): Observable<JobTitleCompetenceModel[]> {
    const queryParams: string[] = [];

    if (params.JobTitleGroupId) {
      queryParams.push(`JobTitleGroupId=${encodeURIComponent(params.JobTitleGroupId)}`);
    }
    if (params.JobTitleFamilyId) {
      queryParams.push(`JobTitleFamilyId=${encodeURIComponent(params.JobTitleFamilyId)}`);
    }
    if (params.JobTitleId) {
      queryParams.push(`JobTitleId=${encodeURIComponent(params.JobTitleId)}`);
    }
    if (params.JobTitleSeniorizationId) {
      queryParams.push(
        `JobTitleSeniorizationId=${encodeURIComponent(params.JobTitleSeniorizationId)}`
      );
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const url = `${this.endpoint}/list-from-competence-matrix${queryString}`;

    return this.http.get(url, null, null, null, true).pipe(
      map((res: any) => {
        const competences = res.body || [];
        return competences.map((competence: any) => JobTitleCompetenceModel.create(competence));
      })
    );
  }
}
